<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- TODO: TOP CLIENT LOGO -->
    <div
      v-if="logoOnTop"
      class="text-center mt-2 p-1"
    >
      <b-link to="/">
        <b-img
          fluid
          :src="`${baseUrl()}/settings/download/logoImage`"
          onerror="this.style.display='none'"
          alt="Logo"
          class="img-responsive"
        />
      </b-link>
    </div>

    <!-- main menu header-->
    <div
      v-if="!logoOnTop"
      class="navbar-header expanded"
    >
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >

        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
              <h2 class="brand-text">
                {{ appName }}
              </h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{ 'd-block': shallShadowBottom }"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items-dynamic class="navigation navigation-main" />

      <vertical-nav-menu-items-my-dashboard
        v-if="isPermitted('myDashboard')"
        class="navigation navigation-main"
      />
      <div>
        <button-add-menu-item
          :item="{ id: 0, addItemIcon: true }"
          :draggable="false"
        />
      </div>

      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main mt-3"
      />

      <div
        v-if="!logoOnTop"
        class="text-center mt-2 p-1"
      >
        <b-img
          fluid
          :src="`${baseUrl()}/settings/download/logoImage`"
          onerror="this.style.display='none'"
          alt="Logo"
          class="img-responsive"
        />
      </div>
      <div
        v-if="logoOnTop"
        class="text-center mt-2 p-1"
      >
        <b-link
          class="navbar-brand"
          to="/"
        >
          <h2 class="brand-text">
            {{ appName }}
          </h2>
        </b-link>
      </div>

    </vue-perfect-scrollbar>
    <!-- /main menu content-->

  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { baseUrl } from '@/libs/axios'
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { provide, computed, ref } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import ButtonAddMenuItem from '@/layouts/components/ButtonAddMenuItem.vue'
import { getUserData, isPermitted } from '@/auth/utils'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import VerticalNavMenuItemsDynamic from './components/vertical-nav-menu-items/VerticalNavMenuItemsDynamic.vue'
import VerticalNavMenuItemsMyDashboard from './components/vertical-nav-menu-items/VerticalNavMenuItemsMyDashboard.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    VerticalNavMenuItemsDynamic,
    VerticalNavMenuItemsMyDashboard,
    BLink,
    BImg,
    ButtonAddMenuItem,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      baseUrl,
      lnks: [],
      userData: null,
      // logoImg: null,
      logoOnTop: false,
      // logoImageInProgress: true,
    }
  },
  // computed: {

  //   logoImgUrl() {
  //     return this.logoImg
  //   },
  // },
  async created() {
    this.userData = getUserData()
    await this.getLogoPosition()
  },
  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
  methods: {
    isPermitted(code) {
      // skip check if pdf export
      if (!this.userData) {
        return false
      }
      return isPermitted(this.userData.role, code, this.userData.userGroups)
    },
    async getLogoPosition() {
      await useJwt.axiosIns.get('settings/logoontop')
        .then(response => {
          if (response.data) {
            if (response.data) {
              this.logoOnTop = response.data
            }
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
